import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { apiClient } from "../../helpers/apiClient";
import { useAuthInfo } from "../../helpers/useAuthInfo";
import { useTranslation } from "react-i18next";
import { getEnumKeyByValue, useWindowDimensions } from "../../helpers/helpers";
import { versions, languages } from "../../helpers/enums";
import { createAssessment, updateAssessment } from "../../graphql/mutations";
import {
  Box,
  Button,
  InputLabel,
  Divider,
  IconButton,
  Modal,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
} from "@mui/material";

export default function NewAssessmentModal({
  dataLabel,
  open,
  setOpen,
  selectedAssessment,
  setNotificationMessage,
  setOpenNotification,
  fetchAllAssessments,
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { userGroups } = useAuthInfo();

  const emptyNewAssessment = {
    id: "",
    name: "",
    description: "",
    contactPerson: "",
    type: "",
    active: false,
    public: false,
    ownerGroups: [],
    language: "",
  };
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    bgcolor: "background.paper",
    borderRadius: 5,
    boxShadow: 24,
    pb: 4,
    pt: 1,
    maxWidth: width,
  };
  const textFieldSx = { width: 400, mb: 2, maxWidth: width - 20 };
  const selectSx = { width: 400, mb: 0, maxWidth: width - 20 };
  const formControlSx = { width: 400, mb: 1, maxWidth: width - 20 };

  const [selectedType, setSelectedType] = useState(versions.STANDARD);
  const [selectedLanguage, setSelectedLanguage] = useState(languages.DE);
  const [newAssessment, setNewAssessment] = useState(emptyNewAssessment);

  function handleNewAssessmentChange(event, key) {
    setNewAssessment({
      ...newAssessment,
      [key]: event.target.value,
    });
  }

  function resetData() {
    setNewAssessment(emptyNewAssessment);
    setSelectedLanguage(languages.DE);
    setSelectedType(versions.STANDARD);
  }

  async function addAssessment() {
    try {
      const version = getEnumKeyByValue(versions, selectedType);
      await apiClient.graphql({
        query: createAssessment,
        variables: {
          input: {
            name: newAssessment.name,
            description: newAssessment.description,
            contactPerson: newAssessment.contactPerson,
            type: version,
            active: false,
            public: false,
            language: selectedLanguage,
            ownerGroups: userGroups,
          },
        },
      });
      resetData();
      setNotificationMessage(`${dataLabel} ${t("added")}`);
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
    fetchAllAssessments();
    setOpen(false);
  }

  async function changeAssessment() {
    try {
      const version = getEnumKeyByValue(versions, selectedType);
      await apiClient.graphql({
        query: updateAssessment,
        variables: {
          input: {
            id: newAssessment.id,
            name: newAssessment.name,
            description: newAssessment.description,
            contactPerson: newAssessment.contactPerson,
            type: version,
            active: false,
            public: false,
            language: selectedLanguage,
          },
        },
      });
      resetData();
      setNotificationMessage(`${dataLabel} ${t("updated")}`);
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
    fetchAllAssessments();
    setOpen(false);
  }

  useEffect(() => {
    if (open) {
      if (selectedAssessment.id !== "") {
        setNewAssessment(selectedAssessment);
        setSelectedLanguage(selectedAssessment.language);
        setSelectedType(selectedAssessment.type);
      } else {
        resetData();
      }
    } else {
      resetData();
    }
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableAutoFocus={true}
      >
        <Box sx={style}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            <IconButton
              color="secondary"
              sx={{ mr: 1 }}
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: 1,
            }}
          >
            <Typography sx={{ fontSize: 18, userSelect: "none" }}>
              {t("createAssessment")}
            </Typography>
            <Divider flexItem sx={{ mb: 4 }} />
            <TextField
              required
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              label="Assessment"
              variant="outlined"
              size="small"
              sx={textFieldSx}
              value={newAssessment.name}
              onChange={(event) => handleNewAssessmentChange(event, "name")}
            />
            <FormControl sx={formControlSx}>
              <InputLabel shrink={true}>Type</InputLabel>
              <Select
                notched={true}
                label={"Type"}
                size="small"
                sx={selectSx}
                value={selectedType}
                onChange={(event) => {
                  handleNewAssessmentChange(event, "type");
                  setSelectedType(event.target.value);
                }}
              >
                {Object.values(versions).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ mx: 0.2, fontSize: 11 }}>
                {t("notChangeable")}
              </FormHelperText>
            </FormControl>
            <TextField
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              label={t("contact")}
              variant="outlined"
              size="small"
              sx={textFieldSx}
              value={newAssessment.contactPerson}
              onChange={(event) =>
                handleNewAssessmentChange(event, "contactPerson")
              }
            />
            <TextField
              color="secondary"
              InputLabelProps={{
                shrink: true,
              }}
              label={t("infotext")}
              variant="outlined"
              size="small"
              sx={textFieldSx}
              value={newAssessment.description}
              multiline
              rows={5}
              onChange={(event) =>
                handleNewAssessmentChange(event, "description")
              }
            />
            <FormControl sx={formControlSx}>
              <InputLabel shrink={true}>{t("language")}</InputLabel>
              <Select
                notched={true}
                label={t("language")}
                sx={selectSx}
                size="small"
                value={selectedLanguage}
                onChange={(event) => {
                  handleNewAssessmentChange(event, "language");
                  setSelectedLanguage(event.target.value);
                }}
              >
                {Object.values(languages).map((lang) => (
                  <MenuItem key={lang} value={lang}>
                    {lang}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              sx={{ mt: 1 }}
              color="secondary"
              variant="contained"
              size="small"
              onClick={() => {
                if (selectedAssessment.id === "") {
                  addAssessment();
                } else {
                  changeAssessment();
                }
              }}
            >
              {selectedAssessment.id === ""
                ? t("createAssessment")
                : t("editAssessment")}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
